import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Download, Filter, Info, ListOrdered, Lock, Map as MapaIcone, X } from 'lucide-react';
import type { Posicao } from '@wms/shared';
import { useMapa } from '@/hooks/consultas';
import { useSessao } from '@/hooks/useSessao';
import { useParamUrl } from '@/hooks/useUrlEstado';
import {
  useEnderecosInventario,
  useInventario,
  type EnderecoInventario,
  type EscopoInventario,
  type ProdutosDoMapa,
} from '@/hooks/inventario';
import { cn, compararNatural, moeda, numero, plural } from '@/lib/utils';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { acaoTexto, Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Cartao, Esqueleto, Kpi } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { NavInventario } from '@/componentes/inventario/NavInventario';
import { COR_TOM, haQuanto, percentualBipado } from '@/componentes/inventario/semaforo';
import { CartaoEndereco, useHoverCartao, type PontoCartao } from '@/componentes/inventario/CartaoEndereco';
import { montarResumo, ResumoPorSku } from '@/componentes/inventario/ResumoPorSku';

/**
 * O mapa do inventário — a visão do analista: rua por rua, o que já foi
 * contado e SE BATEU com a foto do congelamento.
 *
 * O QUE A COR DIZ (e o que não diz)
 * ---------------------------------
 * A cor compara quantidade, ao vivo: a soma do que o coletor enviou contra a
 * foto, produto a produto, no endereço. Antes ela dizia só "teve bipe" —
 * verde mesmo contando 10 onde a foto tinha 1 (no inventário 12, 6 endereços
 * bipados, 1 batia).
 *
 * A divergência OFICIAL é outra coisa: só existe depois que o PBS fecha a
 * contagem (FECHAMENTO_*_RESULTADOS), e é ela que decide quem entra na
 * recontagem. Ela aparece como um ponto escuro no canto da vaga, por cima da
 * cor ao vivo — as duas costumam concordar; quando não, é lote/validade, que a
 * conta ao vivo não olha.
 *
 * ALVO POR CONTAGEM, como no coletor: na 1ª, todo endereço do escopo; na
 * recontagem, só o que divergiu oficialmente na 1ª; na final, só o que divergiu
 * na recontagem. O resto fica cinza ("fora desta contagem").
 *
 * VÁRIOS ESCOPOS: o seletor de escopo recorta tudo — ruas, números, rack,
 * lista e CSV. "Todos" é o inventário inteiro.
 *
 * R$ E TROCA DE VAGA: a diferença vem também em reais (último custo de compra,
 * a regra da prévia de fechamento), e a lista ordena por ela — é o que decide
 * onde recontar primeiro. Quando o mesmo produto sobra numa vaga e falta em
 * outra, quase sempre foi guardado no lugar errado, não perdido: o cartão
 * aponta a outra vaga e o indicador separa o que se compensa.
 *
 * FILTROS: clicar nos estados acende só eles no rack (o resto esmaece, sem
 * sumir, para não perder a geometria da rua) e mostra em cada rua quantos
 * endereços daquele filtro ela tem — "onde estão as sobras?" vira uma olhada.
 */

type Contagem = 0 | 1 | 2;
type Estado =
  | 'falta'
  | 'troca'
  | 'sobra'
  | 'sem_produto'
  | 'pendente'
  | 'confere'
  | 'bipado'
  | 'vazio_conferido'
  | 'vazio'
  | 'fora_do_alvo';

const ROTULO_CONTAGEM: Record<Contagem, string> = { 0: '1ª contagem', 1: 'Recontagem', 2: 'Contagem final' };

const ESTILO: Record<Estado, { celula: string; ponto: string; rotulo: string; dica: string; texto?: string }> = {
  confere: {
    celula: 'border-status-livre bg-status-livre',
    ponto: 'bg-status-livre',
    rotulo: 'Bateu',
    dica: 'Contado igual à foto em todos os produtos',
    texto: 'text-white',
  },
  falta: {
    celula: 'border-status-divergencia bg-status-divergencia',
    ponto: 'bg-status-divergencia',
    rotulo: 'Contou a menos',
    dica: 'Algum produto com menos do que a foto — ou que não foi bipado',
    texto: 'text-white',
  },
  sobra: {
    celula: 'border-primaria bg-primaria',
    ponto: 'bg-primaria',
    rotulo: 'Contou a mais',
    dica: 'Mais do que a foto, ou produto que a foto não tinha ali',
    texto: 'text-white',
  },
  troca: {
    celula: 'border-status-divergencia bg-gradient-to-br from-status-divergencia from-50% to-primaria to-50%',
    ponto: 'bg-gradient-to-br from-status-divergencia from-50% to-primaria to-50%',
    rotulo: 'A menos e a mais',
    dica: 'Um produto faltou e outro sobrou — costuma ser produto trocado de vaga',
    texto: 'text-white',
  },
  sem_produto: {
    celula: 'border-status-atencao bg-status-atencao',
    ponto: 'bg-status-atencao',
    rotulo: 'Visitado sem produto',
    dica: 'Validado no coletor sem nenhum produto: tinha saldo, vira falta ao fechar',
    texto: 'text-white',
  },
  // Azul "a fazer": branco se confundia com vaga vazia, e vermelho/âmbar/roxo
  // já dizem problema. É a mesma cor do aviso "ainda não é divergência".
  pendente: {
    celula: 'border-sky-500 bg-sky-500/20',
    ponto: 'border border-sky-500 bg-sky-500/20',
    rotulo: 'Falta bipar',
    dica: 'Tinha saldo na foto e ninguém passou ainda',
  },
  bipado: {
    celula: 'border-status-livre/60 bg-status-livre/60',
    ponto: 'bg-status-livre/60',
    rotulo: 'Bipado (sem foto)',
    dica: 'Escopo sem congelamento: não há foto para comparar a quantidade',
    texto: 'text-white',
  },
  vazio_conferido: {
    celula: 'border-status-livre/60 bg-status-livreFundo',
    ponto: 'border border-status-livre/60 bg-status-livreFundo',
    rotulo: 'Vazio conferido',
    dica: 'Vazio na foto e confirmado vazio no coletor',
  },
  vazio: {
    celula: 'border-dashed border-borda bg-superficie2',
    ponto: 'border border-dashed border-borda bg-superficie2',
    rotulo: 'Vazio',
    dica: 'Vazio na foto — não precisa de bipe',
  },
  fora_do_alvo: {
    celula: 'border-borda bg-superficie3/60',
    ponto: 'bg-superficie3',
    rotulo: 'Fora desta contagem',
    dica: 'Não divergiu na contagem anterior: o coletor não pede este endereço agora',
  },
};

/** Ordem de leitura: o que exige ação primeiro. Também decide a cor da vaga com vários endereços. */
const ORDEM: Estado[] = [
  'falta',
  'troca',
  'sobra',
  'sem_produto',
  'pendente',
  'confere',
  'bipado',
  'vazio_conferido',
  'vazio',
  'fora_do_alvo',
];

/** Cor de cada estado na barrinha de mistura da rua (sem borda, só o fundo). */
const FATIA: Record<Estado, string> = {
  confere: 'bg-status-livre',
  falta: 'bg-status-divergencia',
  sobra: 'bg-primaria',
  troca: 'bg-gradient-to-r from-status-divergencia to-primaria',
  sem_produto: 'bg-status-atencao',
  pendente: 'bg-sky-400',
  bipado: 'bg-status-livre/60',
  vazio_conferido: 'bg-status-livreFundo',
  vazio: 'bg-superficie3',
  fora_do_alvo: 'bg-superficie3',
};

/** Estados que contam como "do alvo" desta contagem. */
const DO_ALVO = new Set<Estado>(['falta', 'troca', 'sobra', 'sem_produto', 'pendente', 'confere', 'bipado']);
const CONTADOS = new Set<Estado>(['falta', 'troca', 'sobra', 'confere', 'bipado']);

interface Linha {
  produto: number;
  esperado: number | null;
  contado: number;
}

interface Item {
  e: EnderecoInventario;
  p: Posicao | undefined;
  estado: Estado;
  linhas: Linha[];
  /** Unidades a menos (positivo) e a mais, somando os produtos. */
  faltaUn: number;
  sobraUn: number;
  /** O mesmo, em R$ (pelo custo de cada produto). */
  faltaR: number;
  sobraR: number;
  /** Divergência oficial do fechamento desta contagem. */
  oficial: boolean;
}

function avaliar(
  e: EnderecoInventario,
  c: Contagem,
  saldoNoMapa: boolean,
  custo: (produto: number) => number,
): Omit<Item, 'p'> {
  const linhas: Linha[] = e.itens
    .map((i) => ({ produto: i.produto, esperado: i.esperado, contado: i.contado[c] }))
    .filter((l) => l.contado !== 0 || (l.esperado ?? 0) !== 0);
  let faltaUn = 0;
  let sobraUn = 0;
  let faltaR = 0;
  let sobraR = 0;
  for (const l of linhas) {
    const d = l.contado - (l.esperado ?? 0);
    if (d < 0) {
      faltaUn -= d;
      faltaR -= d * custo(l.produto);
    } else {
      sobraUn += d;
      sobraR += d * custo(l.produto);
    }
  }
  const base = { e, linhas, faltaUn, sobraUn, faltaR, sobraR, oficial: e.divergente[c] };

  if (c > 0 && !e.divergente[c - 1]) return { ...base, estado: 'fora_do_alvo' };
  const temSaldo = e.comSaldo ?? saldoNoMapa;
  if (!e.bipado[c]) {
    if (e.visitado[c]) return { ...base, estado: temSaldo ? 'sem_produto' : 'vazio_conferido' };
    return { ...base, estado: temSaldo ? 'pendente' : 'vazio' };
  }
  if (e.comSaldo === null) return { ...base, estado: 'bipado' };
  if (faltaUn > 0 && sobraUn > 0) return { ...base, estado: 'troca' };
  if (faltaUn > 0) return { ...base, estado: 'falta' };
  if (sobraUn > 0) return { ...base, estado: 'sobra' };
  return { ...base, estado: 'confere' };
}

interface Rua {
  nome: string;
  itens: Item[];
  porEstado: Partial<Record<Estado, number>>;
  alvo: number;
  contados: number;
  /** Todo escopo com endereço na rua com esta contagem fechada ou integrado. */
  fechada: boolean;
}

/** O escopo já fechou a contagem `c` no ERP? Integrado conta como fechado. */
function fechou(e: EscopoInventario | undefined, c: Contagem): boolean {
  if (!e) return false;
  if (e.integradoEm) return true;
  return c === 0 ? !!e.contagemFechadaEm : c === 1 ? !!e.recontagemFechadaEm : !!e.finalFechadoEm;
}

function contar(itens: Item[]): Partial<Record<Estado, number>> {
  const r: Partial<Record<Estado, number>> = {};
  for (const i of itens) r[i.estado] = (r[i.estado] ?? 0) + 1;
  return r;
}

function soma(r: Partial<Record<Estado, number>>, estados: Iterable<Estado>): number {
  let n = 0;
  for (const e of estados) n += r[e] ?? 0;
  return n;
}

const nomeDe = (i: Item): string => i.p?.descricao?.trim() || String(i.e.localizador);

/** "−2 / +3" curtinho para dentro da vaga. */
function rotuloDiferenca(i: Item): string | null {
  if (i.estado === 'falta') return `−${i.faltaUn}`;
  if (i.estado === 'sobra') return `+${i.sobraUn}`;
  if (i.estado === 'troca') return '±';
  return null;
}

/**
 * Mesmo produto sobrando numa vaga e faltando noutra, no recorte da tela: quase
 * sempre foi guardado no lugar errado, e não perdido. Para cada produto, o que
 * se compensa é o menor entre o que sobrou e o que faltou; o resto é perda ou
 * ganho de verdade.
 */
interface Trocas {
  /** produto → as vagas onde ele faltou e onde sobrou. */
  porProduto: Map<number, { faltou: { i: Item; un: number }[]; sobrou: { i: Item; un: number }[] }>;
  /** R$ que se compensam entre vagas. */
  valor: number;
}

function trocasDeVaga(itens: Item[], custo: (produto: number) => number): Trocas {
  const porProduto: Trocas['porProduto'] = new Map();
  for (const i of itens) {
    if (!CONTADOS.has(i.estado) || i.estado === 'bipado') continue;
    for (const l of i.linhas) {
      const d = l.contado - (l.esperado ?? 0);
      if (d === 0) continue;
      const g = porProduto.get(l.produto) ?? { faltou: [], sobrou: [] };
      (d < 0 ? g.faltou : g.sobrou).push({ i, un: Math.abs(d) });
      porProduto.set(l.produto, g);
    }
  }
  let valor = 0;
  for (const [produto, g] of porProduto) {
    if (g.faltou.length === 0 || g.sobrou.length === 0) {
      porProduto.delete(produto);
      continue;
    }
    const un = Math.min(
      g.faltou.reduce((s, x) => s + x.un, 0),
      g.sobrou.reduce((s, x) => s + x.un, 0),
    );
    valor += un * custo(produto);
  }
  return { porProduto, valor };
}

/** "Provável troca de vaga: faltou 1 em 060 TC005 601". */
function notaDeTroca(trocas: Trocas, i: Item, l: Linha): string | undefined {
  const g = trocas.porProduto.get(l.produto);
  if (!g) return undefined;
  const d = l.contado - (l.esperado ?? 0);
  if (d === 0) return undefined;
  const outras = (d > 0 ? g.faltou : g.sobrou).filter((x) => x.i !== i);
  if (outras.length === 0) return undefined;
  const onde = outras
    .slice(0, 2)
    .map((x) => `${d > 0 ? '−' : '+'}${numero(x.un)} em ${nomeDe(x.i)}`)
    .join(', ');
  return `Provável troca de vaga: ${d > 0 ? 'faltou' : 'sobrou'} ${onde}${outras.length > 2 ? ` (+${outras.length - 2})` : ''}`;
}

export function InventarioMapa(): JSX.Element {
  const { id: bruto } = useParams();
  const id = Number(bruto);
  const valido = Number.isInteger(id) && id > 0;
  const { temPermissao } = useSessao();
  const detalhe = useInventario(valido ? id : null);
  const enderecos = useEnderecosInventario(valido ? id : null);
  const mapa = useMapa(detalhe.data?.inventario.centro ?? null);
  const [contagem, definirContagem] = React.useState<Contagem>(0);
  const [ruaVista, definirRuaVista] = React.useState<string | null>(null);
  const [escopoUrl, definirEscopoUrl] = useParamUrl('escopo');
  const escopoVisto = Number(escopoUrl) || null;
  const [filtro, definirFiltro] = React.useState<Set<Estado>>(new Set());
  const [soOficial, definirSoOficial] = React.useState(false);
  /** A visão vive na URL: o link da previsão abre na previsão. */
  const [visaoUrl, definirVisaoUrl] = useParamUrl('visao');
  const visao: 'mapa' | 'sku' = visaoUrl === 'sku' ? 'sku' : 'mapa';

  const porId = React.useMemo(
    () => new Map((mapa.data?.posicoes ?? []).map((p) => [p.localizador, p])),
    [mapa.data],
  );
  const escopos = React.useMemo(
    () => [...(detalhe.data?.escopos ?? [])].sort((a, b) => a.escopo - b.escopo),
    [detalhe.data],
  );
  const escopoPorNumero = React.useMemo(() => new Map(escopos.map((e) => [e.escopo, e])), [escopos]);

  // Abre na contagem mais avançada do inventário: abrir na 1ª com a final já
  // fechada mostrava uma previsão que ignorava recontagem e final.
  const contagemInicial = React.useRef(false);
  React.useEffect(() => {
    if (contagemInicial.current || escopos.length === 0) return;
    contagemInicial.current = true;
    definirContagem(escopos.some((e) => fechou(e, 1)) ? 2 : escopos.some((e) => fechou(e, 0)) ? 1 : 0);
  }, [escopos]);

  /** Todos os endereços do inventário, avaliados na contagem escolhida. */
  const todos = React.useMemo<Item[]>(() => {
    const produtos = enderecos.data?.produtos ?? {};
    const custo = (produto: number): number => produtos[produto]?.custo ?? 0;
    return (enderecos.data?.enderecos ?? []).map((e) => {
      const p = porId.get(e.localizador);
      return { ...avaliar(e, contagem, (p?.saldo ?? 0) > 0, custo), p };
    });
  }, [enderecos.data, porId, contagem]);

  const doEscopo = React.useMemo(
    () => (escopoVisto ? todos.filter((i) => i.e.escopo === escopoVisto) : todos),
    [todos, escopoVisto],
  );

  const trocas = React.useMemo(() => {
    const produtos = enderecos.data?.produtos ?? {};
    return trocasDeVaga(doEscopo, (produto) => produtos[produto]?.custo ?? 0);
  }, [doEscopo, enderecos.data]);

  const skus = React.useMemo(
    () =>
      montarResumo(
        doEscopo.map((i) => i.e),
        enderecos.data?.produtos ?? {},
        contagem,
        (loc) => porId.get(loc)?.descricao?.trim() || String(loc),
        (escopo, k) => fechou(escopoPorNumero.get(escopo), k),
      ),
    [doEscopo, enderecos.data, contagem, porId, escopoPorNumero],
  );
  const skusComDiferenca = skus.filter((s) => s.situacao !== 'igual').length;

  const filtrando = filtro.size > 0 || soOficial;
  const passa = React.useCallback(
    (i: Item) => (filtro.size === 0 || filtro.has(i.estado)) && (!soOficial || i.oficial),
    [filtro, soOficial],
  );

  const ruas = React.useMemo<Rua[]>(() => {
    const g = new Map<string, Item[]>();
    for (const i of doEscopo) {
      const nome = i.p?.corredor.trim() || '(fora do mapa)';
      const l = g.get(nome) ?? [];
      l.push(i);
      g.set(nome, l);
    }
    return [...g.entries()]
      .sort(([a], [b]) => compararNatural(a, b))
      .map(([nome, itens]) => {
        const porEstado = contar(itens);
        const escoposDaRua = [...new Set(itens.map((i) => i.e.escopo))];
        return {
          nome,
          itens,
          porEstado,
          alvo: soma(porEstado, DO_ALVO),
          contados: soma(porEstado, CONTADOS),
          fechada:
            escoposDaRua.length > 0 && escoposDaRua.every((e) => fechou(escopoPorNumero.get(e), contagem)),
        };
      });
  }, [doEscopo, contagem, escopoPorNumero]);

  // Abre na rua com mais coisa por resolver; com filtro, na que mais tem do filtro.
  React.useEffect(() => {
    if (ruaVista && ruas.some((r) => r.nome === ruaVista)) return;
    const peso = (r: Rua): number =>
      filtrando ? r.itens.filter(passa).length : r.alvo - (r.porEstado.confere ?? 0);
    const melhor = [...ruas].sort((a, b) => peso(b) - peso(a))[0];
    if (melhor) definirRuaVista(melhor.nome);
  }, [ruas, ruaVista, filtrando, passa]);

  if (!valido) return <EstadoVazio titulo="Inventário inválido" />;
  if (detalhe.isError) return <EstadoErro erro={detalhe.error} />;
  if (enderecos.isError) return <EstadoErro erro={enderecos.error} aoTentarDeNovo={() => void enderecos.refetch()} />;

  const inv = detalhe.data?.inventario;
  const produtos = enderecos.data?.produtos ?? {};
  const carregando = detalhe.isLoading || enderecos.isLoading || mapa.isLoading;
  const porEstado = contar(doEscopo);
  const alvo = soma(porEstado, DO_ALVO);
  const contados = soma(porEstado, CONTADOS);
  const andamento = percentualBipado(contados, alvo);
  const comparaveis = soma(porEstado, ['falta', 'troca', 'sobra', 'confere']);
  const acerto = percentualBipado(porEstado.confere ?? 0, comparaveis);
  const contadosItens = doEscopo.filter((i) => CONTADOS.has(i.estado));
  const faltaUn = contadosItens.reduce((s, i) => s + i.faltaUn, 0);
  const sobraUn = contadosItens.reduce((s, i) => s + i.sobraUn, 0);
  const faltaR = contadosItens.reduce((s, i) => s + i.faltaR, 0);
  const sobraR = contadosItens.reduce((s, i) => s + i.sobraR, 0);
  // Coletor só envia no Encerrar: enquanto a contagem está aberta, o azul pode
  // estar num aparelho que ainda não encerrou.
  const escoposNaVisao = escopos.filter((e) => escopoVisto === null || e.escopo === escopoVisto);
  const contagemAberta = escoposNaVisao.some(
    (e) => e.etapa !== 'montagem' && !fechou(e, contagem) && (contagem === 0 || e.etapa !== 'contagem'),
  );
  const ultimoEnvio =
    escoposNaVisao
      .map((e) => e.ultimaColeta)
      .filter((d): d is string => d !== null)
      .sort()
      .at(-1) ?? null;
  const comDiferenca = soma(porEstado, ['falta', 'troca', 'sobra']);
  const oficiais = doEscopo.filter((i) => i.oficial).length;
  /*
   * Os quadros acima são DA CONTAGEM escolhida (na final, só os recontados: 23
   * endereços no inventário 15). O resultado do inventário é outro número — o
   * mesmo da visão por SKU: oficial onde a final fechou, previsão no resto.
   */
  const resultadoR = skus.reduce((t, s) => t + (s.depois - s.antes) * s.custo, 0);
  const resultadoUn = skus.reduce((t, s) => t + (s.depois - s.antes), 0);
  const perdaR = skus.reduce((t, s) => t + Math.min(0, s.depois - s.antes) * s.custo, 0);
  const ganhoR = skus.reduce((t, s) => t + Math.max(0, s.depois - s.antes) * s.custo, 0);
  const resultadoOficial = skus.length > 0 && skus.every((s) => s.enderecos.every((e) => e.oficial));
  const rua = ruas.find((r) => r.nome === ruaVista) ?? null;

  const alternar = (e: Estado): void =>
    definirFiltro((a) => {
      const n = new Set(a);
      if (n.has(e)) n.delete(e);
      else n.add(e);
      return n;
    });

  const exportar = (): void => {
    const linhas = doEscopo
      .filter(passa)
      .flatMap((i) =>
        (i.linhas.length > 0 ? i.linhas : [null]).map((l) => ({ i, l })),
      );
    const colunas: ColunaCsv<{ i: Item; l: Linha | null }>[] = [
      { cabecalho: 'Escopo', valor: (x) => x.i.e.escopo },
      { cabecalho: 'Rua', valor: (x) => x.i.p?.corredor.trim() ?? '' },
      { cabecalho: 'Endereço', valor: (x) => nomeDe(x.i), texto: true },
      { cabecalho: 'Localizador', valor: (x) => x.i.e.localizador },
      { cabecalho: 'Situação', valor: (x) => ESTILO[x.i.estado].rotulo },
      { cabecalho: 'Divergência oficial', valor: (x) => (x.i.oficial ? 'sim' : '') },
      { cabecalho: 'Produto', valor: (x) => x.l?.produto ?? '' },
      { cabecalho: 'Descrição', valor: (x) => (x.l ? (produtos[x.l.produto]?.descricao ?? '') : '') },
      { cabecalho: 'Esperado', valor: (x) => x.l?.esperado ?? '' },
      { cabecalho: 'Contado', valor: (x) => (x.l ? x.l.contado : '') },
      { cabecalho: 'Diferença', valor: (x) => (x.l ? x.l.contado - (x.l.esperado ?? 0) : '') },
      { cabecalho: 'Custo unitário', valor: (x) => (x.l ? (produtos[x.l.produto]?.custo ?? 0) : '') },
      {
        cabecalho: 'Diferença R$',
        valor: (x) =>
          x.l ? Math.round((x.l.contado - (x.l.esperado ?? 0)) * (produtos[x.l.produto]?.custo ?? 0) * 100) / 100 : '',
      },
      { cabecalho: 'Provável troca de vaga', valor: (x) => (x.l ? (notaDeTroca(trocas, x.i, x.l) ?? '') : '') },
    ];
    baixarCsv(
      `inventario-${id}-mapa-${ROTULO_CONTAGEM[contagem].toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${carimboArquivo()}`,
      linhas,
      colunas,
    );
  };

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      <NavInventario
        inventario={id}
        nome={inv?.nome ?? 'Inventário'}
        podePlanejar={temPermissao('inventario.planejar')}
      />
      <CabecalhoPagina
        titulo="Mapa do inventário"
        descricao={
          visao === 'sku'
            ? 'Como ficaria o estoque de cada SKU se o inventário fosse integrado agora, com as contagens até a escolhida ao lado.'
            : 'Rua por rua: o que já foi contado e se bateu com a foto do congelamento. Passe o mouse na vaga para ver esperado × bipado de cada produto.'
        }
        icone={<MapaIcone className="h-5 w-5" />}
        acoes={
          <div className="flex rounded-lg border border-borda bg-superficie2 p-0.5">
            {([0, 1, 2] as Contagem[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => definirContagem(c)}
                className={cn(
                  'min-h-[36px] rounded-md px-3 text-[13px] font-medium',
                  contagem === c ? 'bg-superficie text-texto shadow-xs ring-1 ring-borda' : 'text-textoFraco',
                )}
              >
                {ROTULO_CONTAGEM[c]}
              </button>
            ))}
          </div>
        }
      />

      {/*
        As duas visões ficam lado a lado, grandes, logo abaixo do título: a
        previsão é a pergunta seguinte ao mapa ("e se fechar agora?"), não um
        relatório escondido. Escopo e contagem valem para as duas.
      */}
      <div className="flex flex-wrap items-center gap-2 border-b border-borda" role="tablist" aria-label="Visão">
        {(
          [
            { valor: 'mapa', rotulo: 'Mapa por rua', icone: <MapaIcone className="h-4 w-4" />, selo: null },
            {
              valor: 'sku',
              rotulo: 'Previsão do resultado por SKU',
              icone: <ListOrdered className="h-4 w-4" />,
              selo: skusComDiferenca,
            },
          ] as const
        ).map((t) => {
          const ativa = visao === t.valor;
          return (
            <button
              key={t.valor}
              type="button"
              role="tab"
              aria-selected={ativa}
              onClick={() => definirVisaoUrl(t.valor === 'mapa' ? null : 'sku')}
              className={cn(
                '-mb-px inline-flex min-h-[40px] items-center gap-2 border-b-2 px-3 text-[13.5px] font-semibold transition-colors',
                ativa ? 'border-primaria text-primaria' : 'border-transparent text-textoFraco hover:text-texto',
              )}
            >
              {t.icone}
              {t.rotulo}
              {t.selo ? (
                <span
                  className="tabular rounded-full bg-status-divergenciaFundo px-1.5 text-[11px] font-bold text-status-divergencia"
                  title="SKUs com falta ou sobra prevista"
                >
                  {numero(t.selo)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {carregando ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((k) => (
              <Esqueleto key={k} className="h-24" />
            ))}
          </div>
          <Esqueleto className="h-[460px]" />
        </div>
      ) : todos.length === 0 ? (
        <EstadoVazio titulo="Este inventário ainda não tem endereço" detalhe="Monte um escopo para ver o mapa." />
      ) : (
        <>
          {escopos.length > 1 ? (
            <SeletorEscopo
              escopos={escopos}
              todos={todos}
              visto={escopoVisto}
              aoEscolher={(n) => {
                definirEscopoUrl(n === null ? null : String(n));
                definirRuaVista(null);
              }}
            />
          ) : null}

          {contagemAberta ? (
            <p className="flex items-start gap-2 rounded-xl border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-[12.5px]">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
              <span>
                <b>O coletor só envia quando a pessoa toca em Encerrar.</b> O que está azul ("falta bipar") pode já estar
                contado num aparelho que ainda não encerrou — confira com a equipe antes de mandar recontar.{' '}
                <span className="text-textoFraco">
                  Último envio {ultimoEnvio ? haQuanto(ultimoEnvio) : 'ainda nenhum'}.
                </span>
              </span>
            </p>
          ) : null}

          {visao === 'sku' ? (
            <ResumoPorSku skus={skus} ate={contagem} rotuloContagem={ROTULO_CONTAGEM[contagem]} inventario={id} />
          ) : (
          <>
          <div className={cn('grid gap-3 sm:grid-cols-2', resultadoOficial ? 'xl:grid-cols-4' : 'xl:grid-cols-5')}>
            <Kpi
              rotulo={resultadoOficial ? 'Resultado do inventário (oficial)' : 'Resultado do inventário (previsão)'}
              valor={
                <span
                  className={cn(
                    'tabular',
                    resultadoR < 0 ? 'text-status-divergencia' : resultadoR > 0 ? 'text-primaria' : '',
                  )}
                >
                  {resultadoR > 0 ? '+' : ''}
                  {moeda(resultadoR)}
                </span>
              }
              nota={
                <>
                  <span className="block">
                    <span className="text-status-divergencia">perda {moeda(perdaR)}</span> ·{' '}
                    <span className="text-primaria">ganho +{moeda(ganhoR)}</span>
                  </span>
                  {resultadoUn > 0 ? '+' : ''}
                  {numero(resultadoUn)} un · todos os endereços{' '}
                  <button
                    type="button"
                    className={acaoTexto}
                    onClick={() => definirVisaoUrl('sku')}
                  >
                    ver por SKU
                  </button>
                </>
              }
              tom={resultadoR === 0 ? 'ok' : 'alerta'}
            />
            <Kpi
              rotulo={`Andamento · ${ROTULO_CONTAGEM[contagem]}`}
              valor={`${andamento.pct}%`}
              nota={`${numero(contados)} de ${numero(alvo)} endereços contados`}
              barra={andamento.pct}
              tom={andamento.tom}
            />
            <Kpi
              rotulo="Bateram com a foto"
              valor={comparaveis === 0 ? '—' : `${acerto.pct}%`}
              nota={
                comparaveis === 0
                  ? 'nada contado ainda'
                  : `${numero(porEstado.confere ?? 0)} de ${numero(comparaveis)} endereços contados`
              }
              barra={comparaveis === 0 ? undefined : acerto.pct}
              tom={comparaveis === 0 ? 'neutro' : acerto.tom}
            />
            {/* Com a final fechada, a diferença de UMA contagem (na final, só os
                recontados) só confunde ao lado do resultado — some. */}
            {resultadoOficial ? null : (
            <Kpi
              rotulo={`Diferença · ${ROTULO_CONTAGEM[contagem]}`}
              valor={
                <span className="tabular text-[0.8em]">
                  <span className={faltaR > 0 ? 'text-status-divergencia' : ''}>−{moeda(faltaR)}</span>
                  <span className="mx-1 text-textoSuave">/</span>
                  <span className={sobraR > 0 ? 'text-primaria' : ''}>+{moeda(sobraR)}</span>
                </span>
              }
              nota={
                <>
                  −{numero(faltaUn)} / +{numero(sobraUn)} un em {numero(comDiferenca)}{' '}
                  {plural(comDiferenca, 'endereço', 'endereços')} contados nesta contagem
                  {trocas.valor > 0 ? (
                    <span className="block font-semibold text-status-atencao">
                      {moeda(trocas.valor)} se compensam entre vagas (provável troca de lugar)
                    </span>
                  ) : null}
                  {oficiais > 0 ? (
                    <span className="block">
                      {numero(oficiais)} com divergência oficial
                      {/* FECHAMENTO_FINAIS_RESULTADOS é o resultado consolidado: cobre todos os endereços. */}
                      {contagem === 2 ? ' no resultado final (todos os endereços)' : ''}
                    </span>
                  ) : null}
                </>
              }
              tom={comDiferenca > 0 ? 'alerta' : 'neutro'}
            />
            )}
            <Kpi
              rotulo="Falta resolver"
              valor={numero((porEstado.pendente ?? 0) + (porEstado.sem_produto ?? 0))}
              nota={`${numero(porEstado.pendente ?? 0)} falta bipar · ${numero(porEstado.sem_produto ?? 0)} visitado sem produto (vira falta)`}
              tom={(porEstado.sem_produto ?? 0) > 0 ? 'alerta' : 'neutro'}
            />
          </div>

          <Cartao className="overflow-hidden p-0">
            <BarraFiltros
              porEstado={porEstado}
              filtro={filtro}
              aoAlternar={alternar}
              soOficial={soOficial}
              oficiais={oficiais}
              aoAlternarOficial={() => definirSoOficial((a) => !a)}
              aoLimpar={() => {
                definirFiltro(new Set());
                definirSoOficial(false);
              }}
              aoExportar={exportar}
            />

            <div className="flex gap-1.5 overflow-x-auto border-b border-borda bg-superficie2 px-2.5 py-2">
              {ruas.map((r) => (
                <ChipRua
                  key={r.nome}
                  rua={r}
                  ativa={r.nome === ruaVista}
                  doFiltro={filtrando ? r.itens.filter(passa).length : null}
                  aoEscolher={() => definirRuaVista(r.nome)}
                />
              ))}
            </div>

            {rua ? (
              <RackDaRua
                rua={rua}
                trocas={trocas}
                produtos={produtos}
                contagem={contagem}
                passa={passa}
                filtrando={filtrando}
                mostrarEscopo={escopoVisto === null && escopos.length > 1}
              />
            ) : null}
          </Cartao>

          <Legenda />
          </>
          )}
        </>
      )}
    </div>
  );
}

/** Um chip por escopo, com o % da 1ª contagem dele — o recorte do mapa inteiro. */
function SeletorEscopo({
  escopos,
  todos,
  visto,
  aoEscolher,
}: {
  escopos: EscopoInventario[];
  todos: Item[];
  visto: number | null;
  aoEscolher: (n: number | null) => void;
}): JSX.Element {
  const chip = (ativo: boolean): string =>
    cn(
      'shrink-0 rounded-lg border px-2.5 py-1.5 text-left leading-tight transition-colors',
      ativo ? 'border-primaria bg-primaria-suave' : 'border-borda bg-superficie hover:border-bordaForte',
    );
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto">
      <span className="rotulo mr-1 shrink-0">Escopo</span>
      <button type="button" className={chip(visto === null)} onClick={() => aoEscolher(null)} aria-pressed={visto === null}>
        <span className={cn('text-[12px] font-bold', visto === null && 'text-primaria')}>Todos</span>
        <span className="tabular block text-[10.5px] text-textoFraco">{numero(escopos.length)} escopos</span>
      </button>
      {escopos.map((e) => {
        const p = e.comSaldo === null ? null : percentualBipado(e.bipadosComSaldo, e.comSaldo);
        const ruas = [...new Set(todos.filter((i) => i.e.escopo === e.escopo).map((i) => i.p?.corredor.trim()).filter(Boolean))];
        const ativo = visto === e.escopo;
        return (
          <button
            key={e.escopo}
            type="button"
            className={chip(ativo)}
            onClick={() => aoEscolher(e.escopo)}
            aria-pressed={ativo}
            title={`Escopo ${e.escopo} · ${e.ruas.join(', ') || 'sem rua'} · ${numero(e.localizadores)} endereços`}
          >
            <span className="flex items-center gap-1.5">
              <span className={cn('h-2 w-2 rounded-full', p ? COR_TOM[p.tom].ponto : 'bg-bordaForte')} />
              <span className={cn('tabular font-mono text-[12px] font-bold', ativo && 'text-primaria')}>#{e.escopo}</span>
              <span className="tabular text-[11px] font-semibold">{p ? `${p.pct}%` : 'não congelado'}</span>
            </span>
            <span className="block max-w-[160px] truncate text-[10.5px] text-textoFraco">
              {ruas.length > 0 ? ruas.join(', ') : '—'} · {numero(e.localizadores)} end.
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BarraFiltros({
  porEstado,
  filtro,
  aoAlternar,
  soOficial,
  oficiais,
  aoAlternarOficial,
  aoLimpar,
  aoExportar,
}: {
  porEstado: Partial<Record<Estado, number>>;
  filtro: Set<Estado>;
  aoAlternar: (e: Estado) => void;
  soOficial: boolean;
  oficiais: number;
  aoAlternarOficial: () => void;
  aoLimpar: () => void;
  aoExportar: () => void;
}): JSX.Element {
  const presentes = ORDEM.filter((e) => (porEstado[e] ?? 0) > 0);
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-borda px-2.5 py-2">
      <Filter className="h-3.5 w-3.5 text-textoFraco" aria-hidden />
      <span className="rotulo mr-1">Mostrar</span>
      {presentes.map((e) => {
        const ativo = filtro.has(e);
        return (
          <button
            key={e}
            type="button"
            onClick={() => aoAlternar(e)}
            aria-pressed={ativo}
            title={ESTILO[e].dica}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors',
              ativo
                ? 'border-texto bg-texto text-superficie'
                : 'border-borda bg-superficie text-texto hover:border-bordaForte',
            )}
          >
            <span className={cn('h-2.5 w-2.5 rounded-sm', ESTILO[e].ponto)} />
            {ESTILO[e].rotulo}
            <span className="tabular font-bold">{numero(porEstado[e] ?? 0)}</span>
          </button>
        );
      })}
      {oficiais > 0 ? (
        <button
          type="button"
          onClick={aoAlternarOficial}
          aria-pressed={soOficial}
          title="Só os endereços que o fechamento do PBS marcou como divergentes nesta contagem"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors',
            soOficial ? 'border-texto bg-texto text-superficie' : 'border-borda bg-superficie hover:border-bordaForte',
          )}
        >
          <span className="h-2 w-2 rounded-full bg-texto ring-2 ring-superficie" />
          Divergência oficial
          <span className="tabular font-bold">{numero(oficiais)}</span>
        </button>
      ) : null}
      {filtro.size > 0 || soOficial ? (
        <Botao variante="fantasma" tamanho="micro" onClick={aoLimpar}>
          <X className="h-3.5 w-3.5" />
          Limpar
        </Botao>
      ) : null}
      <Botao
        variante="secundaria"
        tamanho="micro"
        className="ml-auto"
        onClick={aoExportar}
        title="Endereços e produtos do recorte atual (escopo, contagem e filtro), com esperado, contado e diferença"
      >
        <Download className="h-3.5 w-3.5" />
        Baixar CSV
      </Botao>
    </div>
  );
}

/** A rua no seletor: % contado, a mistura de estados numa barrinha e o filtro. */
function ChipRua({
  rua: r,
  ativa,
  doFiltro,
  aoEscolher,
}: {
  rua: Rua;
  ativa: boolean;
  doFiltro: number | null;
  aoEscolher: () => void;
}): JSX.Element {
  const p = percentualBipado(r.contados, r.alvo);
  const total = r.itens.length;
  const fatias = ORDEM.filter((e) => (r.porEstado[e] ?? 0) > 0 && e !== 'vazio' && e !== 'fora_do_alvo');
  const apagada = doFiltro === 0;
  return (
    <button
      type="button"
      onClick={aoEscolher}
      aria-pressed={ativa}
      className={cn(
        'relative shrink-0 overflow-hidden rounded-lg border px-2.5 pb-2 pt-1.5 text-left leading-tight transition-colors',
        ativa ? 'border-primaria bg-primaria-suave' : 'border-borda bg-superficie hover:border-bordaForte',
        apagada && !ativa && 'opacity-45',
      )}
    >
      <span className="flex items-center gap-1.5">
        <span className={cn('h-2 w-2 rounded-full', COR_TOM[r.alvo === 0 ? 'neutro' : p.tom].ponto)} />
        <span className={cn('font-mono text-[12px] font-bold', ativa && 'text-primaria')}>{r.nome}</span>
        {r.fechada ? <Lock className="h-3 w-3 text-textoFraco" aria-label="Rua fechada nesta contagem" /> : null}
        {doFiltro !== null ? (
          <span
            className={cn(
              'tabular rounded-full px-1.5 text-[10.5px] font-bold',
              doFiltro > 0 ? 'bg-texto text-superficie' : 'bg-superficie3 text-textoFraco',
            )}
            title="Endereços desta rua no filtro"
          >
            {numero(doFiltro)}
          </span>
        ) : null}
      </span>
      <span className="tabular block text-[10.5px] text-textoFraco">
        {r.alvo === 0 ? '—' : `${p.pct}% contado`}
        {(r.porEstado.falta ?? 0) + (r.porEstado.troca ?? 0) + (r.porEstado.sobra ?? 0) > 0 ? (
          <b className="ml-1 text-status-divergencia">
            {(r.porEstado.falta ?? 0) + (r.porEstado.troca ?? 0) + (r.porEstado.sobra ?? 0)} dif.
          </b>
        ) : null}
      </span>
      {/* A mistura da rua: quanto bateu, quanto deu diferença, quanto falta. */}
      <span className="absolute inset-x-0 bottom-0 flex h-1 bg-superficie3">
        {fatias.map((e) => (
          <span
            key={e}
            className={cn('block h-full', FATIA[e])}
            style={{ width: `${((r.porEstado[e] ?? 0) / total) * 100}%` }}
          />
        ))}
      </span>
    </button>
  );
}

function Legenda(): JSX.Element {
  const grupos: { titulo: string; estados: Estado[] }[] = [
    { titulo: 'Contado', estados: ['confere', 'falta', 'sobra', 'troca'] },
    { titulo: 'Falta contar', estados: ['pendente', 'sem_produto'] },
    { titulo: 'Sem saldo na foto', estados: ['vazio', 'vazio_conferido'] },
    { titulo: 'Outros', estados: ['fora_do_alvo', 'bipado'] },
  ];
  return (
    <div className="space-y-2 rounded-xl border border-borda bg-superficie p-3 text-xs">
      <p className="text-textoFraco">
        A cor compara, ao vivo, a quantidade bipada com a <b className="text-texto">foto do congelamento</b>, produto a
        produto. A <b className="text-texto">divergência oficial</b> só existe depois que a contagem é fechada no PBS — é
        ela que manda o endereço para a recontagem — e aparece como{' '}
        <span className="inline-block h-2 w-2 rounded-full bg-texto align-middle ring-2 ring-superficie" /> no canto da
        vaga.
      </p>
      <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-4">
        {grupos.map((g) => (
          <div key={g.titulo}>
            <p className="rotulo mb-1">{g.titulo}</p>
            {g.estados.map((e) => (
              <p key={e} className="flex items-start gap-1.5 py-0.5 text-textoFraco" title={ESTILO[e].dica}>
                <span className={cn('mt-0.5 h-3 w-3 shrink-0 rounded-sm', ESTILO[e].ponto)} />
                <span>
                  <span className="font-semibold text-texto">{ESTILO[e].rotulo}</span> — {ESTILO[e].dica.toLowerCase()}
                </span>
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * A rua de frente: módulos da esquerda para a direita, níveis de baixo para
 * cima — o desenho que o conferente reconhece no corredor. Ao lado, a lista do
 * recorte, com esperado → contado, para mandar alguém lá.
 */
function RackDaRua({
  rua,
  trocas,
  produtos,
  contagem,
  passa,
  filtrando,
  mostrarEscopo,
}: {
  rua: Rua;
  trocas: Trocas;
  produtos: ProdutosDoMapa;
  contagem: Contagem;
  passa: (i: Item) => boolean;
  filtrando: boolean;
  mostrarEscopo: boolean;
}): JSX.Element {
  const { hover, props: hoverDe } = useHoverCartao<Item[]>();
  const comGeo = rua.itens.filter((i) => i.p?.geometriaConfiavel);
  const semGeo = rua.itens.filter((i) => !i.p?.geometriaConfiavel);
  const modulos = [...new Set(comGeo.map((i) => i.p!.moduloOrdem))].sort((a, b) => a - b);
  const niveis = [...new Set(comGeo.map((i) => i.p!.nivelRack))].sort((a, b) => b - a);
  const celula = new Map<string, Item[]>();
  for (const i of comGeo) {
    const k = `${i.p!.moduloOrdem}|${i.p!.nivelRack}`;
    const l = celula.get(k) ?? [];
    l.push(i);
    celula.set(k, l);
  }

  // A lista ao lado: o filtro, ou — sem filtro — o que pede ação.
  const daLista = rua.itens
    .filter((i) => (filtrando ? passa(i) : ['falta', 'troca', 'sobra', 'sem_produto', 'pendente'].includes(i.estado)))
    .sort(
      (a, b) =>
        ORDEM.indexOf(a.estado) - ORDEM.indexOf(b.estado) ||
        b.faltaR + b.sobraR - (a.faltaR + a.sobraR) ||
        b.faltaUn + b.sobraUn - (a.faltaUn + a.sobraUn) ||
        compararNatural(nomeDe(a), nomeDe(b)),
    );

  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0">
        <p className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold">
          Rua {rua.nome}
          <span className="font-normal text-textoFraco">
            · {numero(rua.itens.length)} endereços · {numero(rua.contados)} de {numero(rua.alvo)} contados ·{' '}
            {numero(rua.porEstado.confere ?? 0)} bateram
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-bold',
              rua.fechada ? 'bg-primaria-suave text-primaria' : 'bg-superficie2 text-textoFraco',
            )}
            title="Todos os escopos com endereço nesta rua já fecharam esta contagem no PBS"
          >
            <Lock className="h-3 w-3" />
            {rua.fechada ? `${ROTULO_CONTAGEM[contagem]} fechada` : `${ROTULO_CONTAGEM[contagem]} aberta`}
          </span>
        </p>
        {comGeo.length > 0 ? (
          <div className="overflow-x-auto pb-1">
            <table className="border-separate border-spacing-1">
              <thead>
                <tr>
                  <th />
                  {modulos.map((m) => (
                    <th key={m} className="tabular w-9 text-2xs font-semibold text-textoFraco">
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {niveis.map((n) => (
                  <tr key={n}>
                    <th className="tabular w-9 pr-1 text-right text-2xs font-semibold text-textoFraco">
                      {n === 0 ? 'piso' : `N${n}`}
                    </th>
                    {modulos.map((m) => {
                      const its = celula.get(`${m}|${n}`) ?? [];
                      if (its.length === 0) return <td key={m} className="h-8 w-9" />;
                      const principal =
                        ORDEM.map((e) => its.find((i) => i.estado === e)).find(Boolean) ?? its[0]!;
                      const acesa = !filtrando || its.some(passa);
                      const oficial = its.some((i) => i.oficial);
                      const rotulo = its.length === 1 ? rotuloDiferenca(principal) : null;
                      return (
                        <td key={m}>
                          <div
                            {...hoverDe(its)}
                            aria-label={`${its.map(nomeDe).join(', ')} — ${ESTILO[principal.estado].rotulo}`}
                            className={cn(
                              'relative grid h-8 w-9 cursor-default place-items-center rounded-md border text-[10px] font-bold transition-opacity',
                              ESTILO[principal.estado].celula,
                              ESTILO[principal.estado].texto,
                              !acesa && 'opacity-15',
                            )}
                          >
                            {rotulo}
                            {its.length > 1 ? <span className="text-[9px]">{its.length}</span> : null}
                            {oficial ? (
                              <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-texto ring-2 ring-superficie" />
                            ) : null}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {semGeo.length > 0 ? (
          <div className="mt-3">
            <p className="rotulo mb-1.5">Fora do padrão de rack</p>
            <div className="flex flex-wrap gap-1">
              {semGeo.map((i) => (
                <span
                  key={`${i.e.escopo}:${i.e.localizador}`}
                  {...hoverDe([i])}
                  className={cn(
                    'relative rounded-md border px-2 py-0.5 font-mono text-2xs',
                    ESTILO[i.estado].celula,
                    ESTILO[i.estado].texto,
                    filtrando && !passa(i) && 'opacity-15',
                  )}
                >
                  {nomeDe(i)}
                  {rotuloDiferenca(i) ? <b className="ml-1">{rotuloDiferenca(i)}</b> : null}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {hover ? (
        <DicaDoEndereco itens={hover.alvo} ponto={hover} produtos={produtos} contagem={contagem} trocas={trocas} />
      ) : null}

      <ListaDaRua
        titulo={filtrando ? 'No filtro, nesta rua' : 'Pede ação nesta rua'}
        itens={daLista}
        produtos={produtos}
        trocas={trocas}
        mostrarEscopo={mostrarEscopo}
        vazio={filtrando ? 'Nada do filtro nesta rua.' : 'Nada pendente nem com diferença nesta rua.'}
      />
    </div>
  );
}

/**
 * Hover da vaga: cada produto com CONTADO e ESPERADO lado a lado — "contou 5 ·
 * esperado 4 (+1)". Produto bipado que a foto não tinha ali entra com esperado
 * 0; produto da foto que ninguém bipou, com contado 0.
 */
function DicaDoEndereco({
  itens,
  ponto,
  produtos,
  contagem,
  trocas,
}: {
  itens: Item[];
  ponto: PontoCartao;
  produtos: ProdutosDoMapa;
  contagem: Contagem;
  trocas: Trocas;
}): JSX.Element {
  const principal = ORDEM.map((e) => itens.find((i) => i.estado === e)).find(Boolean) ?? itens[0]!;
  const ultimo =
    itens
      .map((i) => i.e.ultimoBipe)
      .filter((d): d is string => d !== null)
      .sort()
      .at(-1) ?? null;
  const congelado = itens.every((i) => i.e.comSaldo !== null);
  /*
   * Endereço que ninguém bipou NÃO é divergência: "Bipado 0 · Diferença −4"
   * lia como falta. Nele o cartão mostra o que se espera encontrar e diz
   * com todas as letras quando aquilo vira falta.
   */
  const esperaSo = (i: Item): boolean => i.estado === 'pendente' || i.estado === 'sem_produto';
  const naoContado = itens.every(esperaSo);
  const unidades = (estado: Estado): number =>
    itens
      .filter((i) => i.estado === estado)
      .reduce((s, i) => s + i.linhas.reduce((t, l) => t + (l.esperado ?? 0), 0), 0);
  const aviso =
    principal.estado === 'sem_produto'
      ? {
          tom: 'atencao' as const,
          texto: (
            <>
              Validado no coletor <b>sem nenhum produto</b>. Se a contagem for fechada assim, vira{' '}
              <b>falta de {numero(unidades('sem_produto'))} un</b>.
            </>
          ),
        }
      : principal.estado === 'pendente'
        ? {
            tom: 'info' as const,
            texto: (
              <>
                <b>Ainda não é divergência.</b> Nenhum bipe deste endereço chegou nesta contagem. Só vira falta
                {unidades('pendente') > 0 ? ` (−${numero(unidades('pendente'))} un)` : ''} se a contagem for fechada
                sem passar por aqui. Se alguém está contando agora, o bipe só chega quando tocar em Encerrar.
              </>
            ),
          }
        : undefined;
  return (
    <CartaoEndereco
      ponto={ponto}
      aviso={aviso}
      titulo={
        naoContado
          ? `Espera ser contado · ${ROTULO_CONTAGEM[contagem]}`
          : congelado
            ? `Contado × esperado · ${ROTULO_CONTAGEM[contagem]}`
            : 'Contado (escopo sem foto)'
      }
      etiqueta={
        <span className="inline-flex items-center gap-1.5 rounded-md bg-white/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-white/85">
          <span className={cn('h-2 w-2 rounded-sm', ESTILO[principal.estado].ponto)} />
          {ESTILO[principal.estado].rotulo}
        </span>
      }
      rodape={
        <>
          Escopo {[...new Set(itens.map((i) => i.e.escopo))].join(', ')} · último bipe {haQuanto(ultimo)}
          {itens.some((i) => i.oficial) ? ' · divergência oficial no fechamento' : ''}
        </>
      }
      enderecos={itens.map((i) => ({
        localizador: i.e.localizador,
        nome: nomeDe(i),
        // Não contado: só o que a foto espera (sem "Bipado 0", sem diferença).
        linhas: i.linhas.map((l) => ({
          produto: l.produto,
          descricao: produtos[l.produto]?.descricao ?? null,
          ean: produtos[l.produto]?.ean ?? null,
          quantidade: esperaSo(i) ? (l.esperado ?? 0) : l.contado,
          ...(l.esperado !== null && !esperaSo(i)
            ? {
                esperado: l.esperado,
                valorDiferenca: (l.contado - l.esperado) * (produtos[l.produto]?.custo ?? 0),
              }
            : {}),
          ...(esperaSo(i) ? {} : { nota: notaDeTroca(trocas, i, l) }),
        })),
      }))}
    />
  );
}

function ListaDaRua({
  titulo,
  itens,
  produtos,
  trocas,
  mostrarEscopo,
  vazio,
}: {
  titulo: string;
  itens: Item[];
  produtos: ProdutosDoMapa;
  trocas: Trocas;
  mostrarEscopo: boolean;
  vazio: string;
}): JSX.Element {
  return (
    <div className="flex max-h-[520px] flex-col rounded-lg border border-borda">
      <p className="flex items-center justify-between border-b border-borda bg-superficie2 px-3 py-1.5 text-xs font-semibold">
        {titulo}
        <span className="tabular text-textoFraco">
          {numero(itens.length)} {plural(itens.length, 'endereço', 'endereços')}
        </span>
      </p>
      {itens.length === 0 ? (
        <p className="px-3 py-3 text-xs text-textoSuave">{vazio}</p>
      ) : (
        <ul className="min-h-0 flex-1 divide-y divide-borda overflow-y-auto">
          {itens.map((i) => (
            <li key={`${i.e.escopo}:${i.e.localizador}`} className="px-3 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', ESTILO[i.estado].ponto)} />
                <span className="truncate font-mono font-bold">{nomeDe(i)}</span>
                {i.oficial ? (
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-texto" title="Divergência oficial" />
                ) : null}
                <span className="ml-auto shrink-0 text-[10.5px] text-textoFraco">
                  {mostrarEscopo ? `esc. ${i.e.escopo} · ` : ''}
                  {i.faltaR + i.sobraR > 0 ? (
                    <b className={i.faltaR >= i.sobraR ? 'text-status-divergencia' : 'text-primaria'}>
                      {moeda(i.sobraR - i.faltaR)}
                    </b>
                  ) : (
                    ESTILO[i.estado].rotulo.toLowerCase()
                  )}
                </span>
              </div>
              {i.linhas.length > 0 && i.estado !== 'pendente' ? (
                <ul className="mt-0.5 space-y-0.5 pl-[18px]">
                  {i.linhas.map((l) => {
                    const d = l.contado - (l.esperado ?? 0);
                    const nota = notaDeTroca(trocas, i, l);
                    return (
                      <li key={l.produto} className="text-[11px]">
                        <div className="flex items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-textoFraco" title={produtos[l.produto]?.descricao ?? ''}>
                          {l.produto} · {produtos[l.produto]?.descricao ?? '—'}
                        </span>
                        <span className="tabular shrink-0">
                          {l.esperado === null ? '?' : numero(l.esperado)} → <b>{numero(l.contado)}</b>
                          {l.esperado !== null && d !== 0 ? (
                            <b className={cn('ml-1', d < 0 ? 'text-status-divergencia' : 'text-primaria')}>
                              {d > 0 ? '+' : ''}
                              {numero(d)}
                            </b>
                          ) : null}
                        </span>
                        </div>
                        {nota ? <p className="font-semibold text-status-atencao">{nota}</p> : null}
                      </li>
                    );
                  })}
                </ul>
              ) : i.estado === 'pendente' && i.linhas.length > 0 ? (
                <p className="mt-0.5 pl-[18px] text-[11px] text-textoFraco">
                  espera {numero(i.linhas.reduce((s, l) => s + (l.esperado ?? 0), 0))} un em{' '}
                  {numero(i.linhas.length)} {plural(i.linhas.length, 'produto', 'produtos')}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
